import { PageHeader } from '@/components/common/page-header';
import { SectionCard } from '@/components/common/section-card';
import { EmptyState, ErrorState, TableSkeleton } from '@/components/fleet/states';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useUserGeography } from '@/hooks/use-user-geography';
import { countryFlag } from '@/lib/country-flag';

const NUMBER = new Intl.NumberFormat('en-US');

export function UserGeographyPage(): JSX.Element {
  const { data, isLoading, isError, error, refetch } = useUserGeography();

  return (
    <div className="space-y-6">
      <PageHeader
        title="Users"
        description="Registered accounts by resolved country of origin."
      />

      <SectionCard
        title="User base by country"
        description={
          data && data.unknown_count > 0
            ? `${NUMBER.format(data.unknown_count)} accounts could not be resolved to a country.`
            : undefined
        }
      >
        {isLoading && <TableSkeleton rows={8} />}

        {isError && (
          <ErrorState
            title="Couldn't load user geography."
            error={error}
            onRetry={() => void refetch()}
          />
        )}

        {data && data.countries.length === 0 && <EmptyState message="No users yet" />}

        {data && data.countries.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Country</TableHead>
                <TableHead className="text-right">Users</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.countries.map((c) => (
                <TableRow key={c.country_code}>
                  <TableCell>
                    <span className="mr-1.5" aria-hidden>
                      {countryFlag(c.country_code)}
                    </span>
                    {c.country_name}
                  </TableCell>
                  <TableCell className="text-right font-mono">{NUMBER.format(c.users)}</TableCell>
                </TableRow>
              ))}
              {data.unknown_count > 0 && (
                <TableRow>
                  <TableCell className="text-muted-foreground">Unknown</TableCell>
                  <TableCell className="text-right font-mono text-muted-foreground">
                    {NUMBER.format(data.unknown_count)}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </SectionCard>
    </div>
  );
}
